import React from 'react';
import {t} from '@lingui/core/macro';
import cn from 'classnames';

import Print from 'shared/ui/icons/General/Print';
import SavePdf from 'shared/ui/icons/General/SavePdf';
import SuccessfulMonitor from 'shared/ui/icons/SuccessfulMonitor';
import ProblemMonitor from 'shared/ui/icons/ProblemMonitor';
import PauseMonitor from 'shared/ui/icons/PauseMonitor';
import {getFormattedDate} from 'shared/lib/datetime';

import {OverallKind} from '../../../lib/statusPageHelper';

import styles from './StatusPageHero.module.scss';

interface IStatusPageHeroProps {
    name: string,
    description?: string,
    overall: OverallKind,
    lastCheckedAt: string,
    onDownloadPdf?: () => void,
    isPdfLoading?: boolean,
    onPrint?: () => void,
    hideActions?: boolean,
}

const HERO_VARIANT: Record<OverallKind, string> = {
    operational: styles.ok,
    partial: styles.partial,
    major: styles.major,
    paused: styles.paused,
};

const BADGE: Record<OverallKind, {icon: React.ReactNode, text: () => string}> = {
    operational: {icon: <SuccessfulMonitor/>, text: () => t`Все системы работают нормально`},
    partial: {icon: <ProblemMonitor/>, text: () => t`Частичные проблемы`},
    major: {icon: <ProblemMonitor/>, text: () => t`Обнаружены проблемы`},
    paused: {icon: <PauseMonitor/>, text: () => t`Мониторинг приостановлен`},
};

const formatUpdatedAgo = (iso: string): string => {
    const diffMs = Date.now() - new Date(iso).getTime();
    const minutes = Math.floor(diffMs / 60000);

    if (Number.isNaN(minutes) || minutes < 0) {
        return `${t`Обновлено`} ${getFormattedDate(iso)}`;
    }
    if (minutes < 1) {
        return t`Обновлено только что`;
    }
    if (minutes < 60) {
        return `${t`Обновлено`} ${minutes} ${t`мин. назад`}`;
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
        return `${t`Обновлено`} ${hours} ${t`ч. назад`}`;
    }
    return `${t`Обновлено`} ${getFormattedDate(iso)}`;
};

const StatusPageHero: React.FC<IStatusPageHeroProps> = ({
    name,
    description,
    overall,
    lastCheckedAt,
    onDownloadPdf,
    isPdfLoading,
    onPrint,
    hideActions,
}) => {
    const badge = BADGE[overall];
    return (
        <header className={cn(styles.hero, HERO_VARIANT[overall])}>
            <div className={styles.inner}>
                <div className={styles.bar}>
                    <h1 className={styles.name} title={name}>{name}</h1>

                    {!hideActions && (
                        <div className={styles.actions}>
                            <button
                                type="button"
                                className={styles.actionButton}
                                onClick={onPrint}
                                title={t`Печать`}
                                aria-label={t`Печать`}
                            >
                                <Print/>
                            </button>

                            <button
                                type="button"
                                className={cn(
                                    styles.actionButton,
                                    (isPdfLoading || !onDownloadPdf) && styles.actionButtonDisabled,
                                )}
                                disabled={isPdfLoading || !onDownloadPdf}
                                onClick={onDownloadPdf}
                                title={t`Скачать PDF`}
                                aria-label={t`Скачать PDF`}
                            >
                                {isPdfLoading ? <span className={styles.spinner}/> : <SavePdf/>}
                            </button>
                        </div>
                    )}
                </div>

                <div className={styles.badgeWrapper}>
                    {description?.trim() && (
                        <p className={styles.description}>{description}</p>
                    )}
                    <div className={cn(styles.badge, styles[`badge_${overall}`])}>
                        <span className={styles.badgeIcon}>{badge.icon}</span>
                        <span className={styles.badgeText}>{badge.text()}</span>
                    </div>
                    <div className={styles.updated}>{formatUpdatedAgo(lastCheckedAt)}</div>
                </div>
            </div>
        </header>
    );
};

export default StatusPageHero;
